import { useEffect, useState } from "react"
import { ArrowLeft, Check, Inbox, LoaderCircle, Send } from "lucide-react"
import { router } from "@inertiajs/react"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  loadOutOfScopeOptions,
  priceOutOfScope,
  sendOutOfScopeRequest,
} from "@/lib/outOfScopeRequests"

import RequestAlert from "./RequestAlert"
import RequestCost from "./RequestCost"

const EMPTY = {
  project_name: "",
  title: "",
  subtitle: "",
  hours: "1",
  due_days: "1",
  requirements: "",
  admin_approval_code: "",
  discount_code: "",
}

// Ask a freelancer for extra hours outside the package (UPDA-74) — the work the
// legacy dashboard calls "Out of Scope". Atrium had no way to ask for it.
//
// Compose → review → sent, matching the sample and invitation flows on this
// same profile: all three commit money to a named person, so none of them sends
// on one click.
//
// Two rules shape the form. Past the hour limit the request needs an approval
// code support issues, so that field appears the moment the customer crosses
// it. And the price is never worked out here — every change of hours re-asks
// the server, together with any discount code, because whether a code can be
// used at all depends on the hours it is applied to.
export default function OutOfScopeRequestDialog({ open, freelancer, onOpenChange }) {
  const [options, setOptions] = useState(null)
  const [form, setForm] = useState(EMPTY)
  const [agreed, setAgreed] = useState(false)
  const [quote, setQuote] = useState({ pricing: null, loading: false })
  const [appliedCode, setAppliedCode] = useState("")
  const [codeNote, setCodeNote] = useState({ applied: false, text: "" })
  const [step, setStep] = useState("compose")
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")
  const [finishProfile, setFinishProfile] = useState("")

  const name = freelancer?.penName ?? options?.freelancer?.name ?? "this freelancer"
  const firstName = name.split(" ")[0]

  useEffect(() => {
    if (!open || !freelancer?.id) return undefined

    const controller = new AbortController()
    setOptions(null)
    setForm(EMPTY)
    setAgreed(false)
    setQuote({ pricing: null, loading: false })
    setAppliedCode("")
    setCodeNote({ applied: false, text: "" })
    setStep("compose")
    setError("")
    setFinishProfile("")
    setLoading(true)

    loadOutOfScopeOptions(freelancer.id, { signal: controller.signal })
      .then((data) => {
        setOptions(data)
        setForm((current) => ({
          ...current,
          hours: String(data.defaultHours ?? 1),
          due_days: String(data.defaultDueDays ?? 1),
        }))
        setQuote({ pricing: data.pricing, loading: false })
        if (!data.eligible) setError(data.reason ?? "")
      })
      .catch((loadError) => {
        if (loadError.name !== "AbortError") setError(loadError.message)
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })

    return () => controller.abort()
  }, [open, freelancer?.id])

  // Re-quote whenever the hours or the applied code change. Debounced, because
  // the hours field fires on every keystroke, and aborted on the way out so a
  // slow earlier answer can never land on top of a newer one.
  useEffect(() => {
    if (!open || !freelancer?.id || !options?.eligible) return undefined

    const hours = Number(form.hours)
    if (!Number.isFinite(hours) || hours <= 0) return undefined

    const controller = new AbortController()
    const timer = setTimeout(() => {
      setQuote((current) => ({ ...current, loading: true }))

      priceOutOfScope(
        freelancer.id,
        { hours: form.hours, code: appliedCode },
        { signal: controller.signal }
      )
        .then((data) => {
          setQuote({ pricing: data.pricing, loading: false })
          if (!data.code) return

          setCodeNote(
            data.code.applied
              ? {
                  applied: true,
                  text:
                    data.code.type === "fixed"
                      ? `$${data.code.value} off applied`
                      : `${data.code.value}% off applied`,
                }
              : { applied: false, text: data.code.error }
          )
        })
        .catch((priceError) => {
          if (priceError.name === "AbortError") return
          setQuote((current) => ({ ...current, loading: false }))
        })
    }, 320)

    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [open, freelancer?.id, options?.eligible, form.hours, appliedCode])

  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value }))

  async function submit() {
    if (submitting) return
    setSubmitting(true)
    setError("")
    setFinishProfile("")
    try {
      await sendOutOfScopeRequest(freelancer.id, form)
      setStep("sent")
    } catch (submitError) {
      setError(submitError.message)
      setFinishProfile(submitError.finishProfile ?? "")
      setStep("compose")
    } finally {
      setSubmitting(false)
    }
  }

  const hours = Number(form.hours)
  const days = Number(form.due_days)
  const hourLimit = options?.hourLimit ?? 5
  const needsApproval = Number.isFinite(hours) && hours > hourLimit
  const complete =
    options?.eligible &&
    form.project_name.trim() !== "" &&
    form.title.trim() !== "" &&
    form.subtitle.trim() !== "" &&
    form.requirements.trim() !== "" &&
    Number.isFinite(hours) &&
    hours > 0 &&
    Number.isFinite(days) &&
    days >= 1 &&
    agreed &&
    (!needsApproval || form.admin_approval_code.trim() !== "")

  if (step === "sent") {
    return (
      <>
        <DialogHeader>
          <div className="mb-2 flex size-11 items-center justify-center rounded-full bg-done/10 text-done">
            <Check className="size-5" strokeWidth={2.5} />
          </div>
          <DialogTitle>Request sent to {firstName}</DialogTitle>
          <DialogDescription>
            You'll find it under Requests until {firstName} answers, and you can cancel it there any
            time before it's accepted.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Done</Button>
          <Button
            onClick={() => { onOpenChange(false); router.visit("/requests") }}
            className="gap-1.5"
          >
            <Inbox className="size-4" strokeWidth={2} />
            View requests
          </Button>
        </DialogFooter>
      </>
    )
  }

  if (step === "review") {
    return (
      <>
        <DialogHeader>
          <DialogTitle>Review your request</DialogTitle>
          <DialogDescription>Check it over before {firstName} sees it.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 rounded-[12px] border border-border bg-secondary/45 p-4 text-[13px]">
          <div>
            <span className="text-ink-3">Freelancer</span>
            <div className="font-semibold">{name}</div>
          </div>
          <div>
            <span className="text-ink-3">Work</span>
            <div className="font-semibold">
              {form.hours} {hours === 1 ? "hour" : "hours"} · within {form.due_days}{" "}
              {days === 1 ? "day" : "days"}
            </div>
          </div>
          <div>
            <span className="text-ink-3">Title</span>
            <div className="font-semibold">{form.title}{form.subtitle ? ` — ${form.subtitle}` : ""}</div>
          </div>
          <div>
            <span className="text-ink-3">What you need done</span>
            <div className="mt-1 whitespace-pre-wrap">{form.requirements}</div>
          </div>
          {needsApproval && (
            <div>
              <span className="text-ink-3">Approval code</span>
              <div className="font-semibold">{form.admin_approval_code}</div>
            </div>
          )}
        </div>
        <RequestCost pricing={quote.pricing} loading={quote.loading} />
        {error && <p role="alert" className="text-[12.5px] text-destructive">{error}</p>}
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => setStep("compose")} disabled={submitting}>
            <ArrowLeft className="size-4" />
            Back
          </Button>
          <Button onClick={submit} disabled={submitting} className="gap-1.5">
            {submitting ? <LoaderCircle className="size-4 animate-spin" /> : <Send className="size-4" />}
            {submitting ? "Sending…" : "Send request"}
          </Button>
        </DialogFooter>
      </>
    )
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Request extra hours from {name}</DialogTitle>
        <DialogDescription>
          Hourly work outside your original package. {firstName} can accept or decline; you're only
          charged if it's accepted.
        </DialogDescription>
      </DialogHeader>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-10 text-[13px] text-ink-3">
          <LoaderCircle className="size-4 animate-spin" />
          Opening the form…
        </div>
      ) : !options?.eligible ? (
        <div className="rounded-[12px] border border-border bg-secondary/40 p-5 text-center text-[13px] text-ink-3">
          {options?.reason ?? error ?? "This freelancer can't take extra hours right now."}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="rounded-[12px] border border-border bg-accent px-3.5 py-3 text-[12.5px] leading-[1.55] text-muted-foreground">
            {options.advice}
          </p>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="oos-project-name">Project name</Label>
            <Input
              id="oos-project-name"
              value={form.project_name}
              onChange={(event) => set("project_name")(event.target.value)}
              placeholder="What you'll call this in your project list"
              maxLength={120}
            />
          </div>

          <div className="grid grid-cols-2 gap-3 max-[479px]:grid-cols-1">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="oos-title">Title</Label>
              <Input
                id="oos-title"
                value={form.title}
                onChange={(event) => set("title")(event.target.value)}
                maxLength={200}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="oos-subtitle">Subtitle</Label>
              <Input
                id="oos-subtitle"
                value={form.subtitle}
                onChange={(event) => set("subtitle")(event.target.value)}
                maxLength={200}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 max-[479px]:grid-cols-1">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="oos-hours">Hours</Label>
              <Input
                id="oos-hours"
                type="number"
                inputMode="decimal"
                min={options.hourStep}
                step={options.hourStep}
                value={form.hours}
                onChange={(event) => set("hours")(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="oos-days">Days to complete</Label>
              <Input
                id="oos-days"
                type="number"
                inputMode="numeric"
                min={1}
                step={1}
                value={form.due_days}
                onChange={(event) => set("due_days")(event.target.value)}
              />
            </div>
          </div>

          {needsApproval && (
            <div className="flex flex-col gap-1.5 rounded-[12px] border border-needs-you/35 bg-needs-you/10 px-3.5 py-3">
              <Label htmlFor="oos-approval">Approval code</Label>
              <p className="text-[11.5px] leading-[1.5] text-muted-foreground">
                More than {hourLimit} hours needs a code from support. Email{" "}
                <a href="mailto:support@dibbly.com" className="font-semibold text-primary underline">
                  support@dibbly.com
                </a>{" "}
                and we'll send you one.
              </p>
              <Input
                id="oos-approval"
                value={form.admin_approval_code}
                onChange={(event) => set("admin_approval_code")(event.target.value)}
                placeholder="The code support gave you"
                maxLength={64}
              />
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="oos-requirements">What you need done</Label>
            <Textarea
              id="oos-requirements"
              value={form.requirements}
              onChange={(event) => set("requirements")(event.target.value)}
              placeholder="The scope, the deliverables, and anything that has to be true when it's finished."
              rows={4}
              maxLength={2_000}
            />
          </div>

          <div className="flex items-start gap-2.5">
            <Checkbox
              id="oos-terms"
              checked={agreed}
              onCheckedChange={(checked) => setAgreed(checked === true)}
              className="mt-0.5"
            />
            <Label htmlFor="oos-terms" className="text-[13px] leading-[1.5] font-normal">
              I've read and understood the out-of-scope{" "}
              <a
                href={options.termsUrl}
                target="_blank"
                rel="noreferrer"
                onClick={(event) => event.stopPropagation()}
                className="font-semibold text-primary underline"
              >
                terms and conditions
              </a>
            </Label>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="oos-discount">Discount code</Label>
            <div className="flex gap-2">
              <Input
                id="oos-discount"
                value={form.discount_code}
                onChange={(event) => {
                  set("discount_code")(event.target.value)
                  setAppliedCode("")
                  setCodeNote({ applied: false, text: "" })
                }}
                placeholder="Optional"
                className="flex-1"
              />
              <Button
                variant="outline"
                onClick={() => setAppliedCode(form.discount_code.trim())}
                disabled={!form.discount_code.trim() || quote.loading}
              >
                {quote.loading ? <LoaderCircle className="size-4 animate-spin" /> : "Apply"}
              </Button>
            </div>
            {codeNote.text && (
              <span className={codeNote.applied ? "text-[11.5px] text-done" : "text-[11.5px] text-destructive"}>
                {codeNote.text}
              </span>
            )}
          </div>

          <RequestCost pricing={quote.pricing} loading={quote.loading} />
        </div>
      )}

      {options?.eligible && <RequestAlert error={error} finishProfile={finishProfile} />}

      <DialogFooter>
        <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button onClick={() => setStep("review")} disabled={!complete || loading}>
          Review request
        </Button>
      </DialogFooter>
    </>
  )
}
